import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  HostListener
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SettingsService } from '@delon/theme';
import { CurrencyMaskService } from 'src/app/core/util/currency-mask.service';

@Component({
  selector: 'app-currency-input',
  templateUrl: './currency-input.component.html',
  styles: [],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true
    }
  ]
})
export class CurrencyInputComponent implements ControlValueAccessor {
  inputValue: string = '';

  private _value: number = 0;
  get value(): number {
    return this._value;
  }
  @Input() set value(value: any) {
    this.writeValue(value);
  }
  @Input() disabled = false;
  @Input() maxLength: string | number | null = null;
  @Input() borderless = false;
  @Input() ignoreZero = false;
  @Input() allowEdit = true;
  @Input() allowNegative = false;
  @Input() tranxType: 'sales' | 'purchase' | 'stock' | null = null;
  @Input() tabIndex?: number;
  @Output() readonly blur = new EventEmitter();
  @Output() readonly changing = new EventEmitter<{
    oldValue: number;
    newValue: number;
  }>();
  @Input() protected = false;

  get readonly() {
    if (this.disabled || !this.allowEdit) {
      return true;
    }
    return false;
  }

  get precision(): number {
    if (this.tranxType === 'sales') {
      return this.settings.user['company'].salesPricePrecision;
    }
    if (this.tranxType === 'purchase') {
      return this.settings.user['company'].purchasePricePrecision;
    }
    if (this.tranxType === 'stock') {
      return this.settings.user['company'].stockPricePrecision;
    }
    return 2;
  }

  constructor(
    private currencySrv: CurrencyMaskService,
    private cdr: ChangeDetectorRef,
    private settings: SettingsService
  ) {}

  onChange: any = () => {};
  onTouched: any = () => {};
  writeValue(value: any): void {
    const newVal = +this.currencySrv.parse(
      value,
      this.precision,
      this.allowNegative
    );
    if (newVal !== this._value) {
      this._value = newVal;
      this.formatValue();
    }
    // initialize
    else if (newVal === 0 && this._value === 0) {
      this.formatValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onFocus(event: any) {
    event.target.value = this.currencySrv.format(
      this.value,
      this.precision,
      this.allowNegative
    );
    event.target.select();
  }

  onModelChanged() {
    this.parseValue(false);
    this.cdr.markForCheck();
  }

  onBlur() {
    this.onTouched();
    this.parseValue(true);
    // Update display text in input
    this.formatValue();
    this.blur.emit();
  }

  onEnter() {
    this.onTouched();
    this.onChange(this._value);
    // Update display text in input
    this.formatValue();
  }

  onPaste(event: ClipboardEvent) {
    var clipData = event.clipboardData?.getData('text');

    if (clipData) {
      const alphaRegex = /[a-zA-Z]/;
      var isInvalid = alphaRegex.test(clipData);

      if (isInvalid) event.preventDefault();
    } else {
      event.preventDefault();
    }
  }

  onKeydown(event: any) {
    if (event.ctrlKey || event.metaKey) return;

    const key = event.which || event.keyCode || 0;

    if (
      key === 8 || // Backspace
      key === 9 || // Tab
      key === 46 || // Del
      key === 37 || // Left Arrow
      key === 39 || // Arraw Right
      key === 13 || // Enter Key
      (event.key === '-' && this.allowNegative) || // Leading negative symbol
      (!!!event.shiftKey && event.key === '.') || // decimal point
      (!!!event.shiftKey && key >= 47 && key <= 57) || // Number Key
      (key >= 96 && key <= 105) // NumPad
    ) {
      return;
    } else {
      event.preventDefault();
    }
  }

  private parseValue(validate: boolean) {
    let numberVal = +this.currencySrv.parse(
      this.inputValue,
      this.precision,
      this.allowNegative
    );

    if (validate) {
      const event = {
        oldValue: this._value,
        newValue: numberVal
      };
      this.changing.emit(event);

      numberVal = event.newValue;
    }
    this._value = numberVal;

    this.onChange(this._value);
    this.cdr.markForCheck();
  }

  private formatValue() {
    if (!this._value && this.ignoreZero) {
      this.inputValue = '';
    } else {
      this.inputValue = this.currencySrv.format(
        this.value,
        this.precision,
        this.allowNegative
      );
    }
  }

  get text() {
    if (!this.value && this.ignoreZero) {
      return '';
    }

    return this.currencySrv.format(
      this.value,
      this.precision,
      this.allowNegative
    );
  }
}
